/**
 * Type-safe analytics composable
 * 
 * Provides a consistent interface for tracking events across the application.
 * Uses nuxt-gtag module for better integration and type safety.
 * All events are typed to ensure data quality and prevent errors.
 */

// Define all possible analytics events with their parameters
export type AnalyticsEvent =
  | { name: 'page_view'; params: { page_path: string; page_title: string } }
  | { name: 'join_form_start'; params: { source?: string } }
  | { name: 'join_form_submit'; params: { success: boolean; error?: string } }
  | { name: 'newsletter_signup'; params: { source: string } }
  | { name: 'artist_click'; params: { artist_slug: string; position?: number } }
  | { name: 'collection_view'; params: { collection_id: string; collection_name: string } }
  | { name: 'external_link_click'; params: { url: string; link_text: string } }
  | { name: 'image_view'; params: { image_url: string; context: string } }
  | { name: 'error_occurred'; params: { error_type: string; error_message: string; page_path: string } }

/**
 * Analytics composable for tracking user events
 * 
 * Uses nuxt-gtag's useGtag composable for type-safe event tracking.
 * 
 * @example
 * ```ts
 * const { track, trackPageView } = useAnalytics()
 * 
 * track({
 *   name: 'join_form_submit',
 *   params: { success: true }
 * })
 * ```
 */
export const useAnalytics = () => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const { gtag } = useGtag()

  /**
   * Track a custom event
   */
  const track = (event: AnalyticsEvent) => {
    // Only track on client-side
    if (!process.client) return

    // Check if analytics is enabled
    const gaId = config.public.gaId as string | undefined
    if (!gaId) return

    // Check consent if required
    const requireConsent = config.public.requireConsent === true
    if (requireConsent) {
      const consentCookie = useCookie<boolean | null>('analytics_consent', { default: () => null })
      // Only track if user has explicitly granted consent (value is true)
      if (consentCookie.value !== true) {
        // In dev, log that consent is required
        if (import.meta.env.DEV) {
          console.debug('[Analytics] Event blocked - consent required:', event.name)
        }
        return
      }
    }

    // Send event to Google Analytics using nuxt-gtag
    gtag('event', event.name, event.params)
    
    // Log in development
    if (import.meta.env.DEV) {
      console.log('[Analytics]', event.name, event.params)
    }
  }

  /**
   * Track a page view
   * Automatically extracts page path and title from current route
   * 
   * Note: nuxt-gtag automatically tracks page views on route changes,
   * but this method can be used for manual tracking if needed.
   */
  const trackPageView = () => {
    if (!process.client) return

    track({
      name: 'page_view',
      params: {
        page_path: route.path,
        page_title: document.title
      }
    })
  }

  return {
    track,
    trackPageView
  }
}
